import type { Track } from '@/world/Track';
import type { DifficultyLevel } from '@/config/tuning';
import type { CourseExtras } from '@/config/cities';
import { SimCore, type RacerInput, type SimEvent } from '@/sim/SimCore';
import type { SimRacer } from '@/sim/SimRacer';
import { getPlayerVehicle } from '@/entities/vehicles';
import { AuthoritativeRoom } from '@/net/AuthoritativeRoom';
import { ClientSession, type RacerPose } from '@/net/ClientSession';
import { LoopbackLink, type LinkConfig } from '@/net/LoopbackLink';
import type { Snapshot } from '@/net/protocol';
import type { NetDriver } from '@/net/NetDriver';

interface SnapMsg {
  t: 'snap';
  snap: Snapshot;
}
interface EvMsg {
  t: 'ev';
  events: SimEvent[];
}
interface UpMsg {
  tick: number;
  input: RacerInput;
}

export interface LoopbackOpts {
  track: Track;
  difficulty: DifficultyLevel;
  course: CourseExtras | undefined;
  seed: number;
  fieldSize: number;
  /** Which racer the local player controls (default 0). */
  localId?: number;
  link?: Partial<LinkConfig>;
  /** Snapshot cadence in sim ticks (default 6 = 20 Hz at 120 Hz). */
  snapEvery?: number;
  rand?: () => number;
}

/**
 * LoopbackDriver.ts — runs a full online race ENTIRELY in one process: an
 * authoritative {@link AuthoritativeRoom} (host) and a {@link ClientSession}
 * (the local player) wired through a {@link LoopbackLink} that fakes latency,
 * jitter and loss. The Phase-2 demo + the headless tests. The same client/host
 * pieces that the WsDriver splits across a real network.
 */
export class LoopbackDriver implements NetDriver {
  host!: AuthoritativeRoom;
  client!: ClientSession;
  private link!: LoopbackLink;

  private readonly localId: number;
  private readonly snapEvery: number;
  private readonly dt: number;
  private pendingEvents: SimEvent[] = [];

  constructor(
    private readonly opts: LoopbackOpts,
    dt: number,
  ) {
    this.dt = dt;
    this.localId = opts.localId ?? 0;
    this.snapEvery = opts.snapEvery ?? 6;
    this.build();
  }

  private build(): void {
    const o = this.opts;
    const hostSim = new SimCore(o.track, o.difficulty, o.course, o.seed, o.fieldSize);
    const clientSim = new SimCore(o.track, o.difficulty, o.course, o.seed, o.fieldSize);
    // M51: the local pick drives slot 0 on BOTH sims — host and prediction
    // must agree byte-for-byte (the loopback is the determinism proving ground).
    hostSim.setPlayerVehicle(getPlayerVehicle());
    clientSim.setPlayerVehicle(getPlayerVehicle());
    this.host = new AuthoritativeRoom(hostSim);
    this.client = new ClientSession(clientSim, this.localId, this.dt);
    this.link = new LoopbackLink({ latencyMs: 60, jitterMs: 20, loss: 0.03, ...o.link }, o.rand);
    this.pendingEvents = [];
  }

  reset(): void {
    this.build();
  }

  setLink(cfg: Partial<LinkConfig>): void {
    this.link.setConfig(cfg);
  }

  /** One fixed step of the whole loop. */
  tick(localInput: RacerInput): void {
    this.link.tick(
      this.dt * 1000,
      (up) => {
        const m = up as UpMsg;
        this.host.applyInput(this.localId, m.input, m.tick);
      },
      (down) => {
        const m = down as SnapMsg | EvMsg;
        if (m.t === 'snap') this.client.applySnapshot(m.snap);
        else this.pendingEvents.push(...m.events);
      },
    );

    const events = this.host.step(this.dt);
    if (this.host.serverTick % this.snapEvery === 0) {
      this.link.sendDown({ t: 'snap', snap: this.host.snapshot() } satisfies SnapMsg);
    }
    if (events.length) this.link.sendDown({ t: 'ev', events } satisfies EvMsg);

    this.client.predict(localInput);
    this.link.sendUp({ tick: this.client.predictor.clientTick - 1, input: localInput } satisfies UpMsg);
  }

  poses(alpha: number, frameDt: number): RacerPose[] {
    return this.client.poses(alpha, frameDt);
  }

  get racers(): SimRacer[] {
    return this.client.predictor.racers;
  }

  get player(): SimRacer {
    return this.client.predictor.local();
  }

  takeEvents(): SimEvent[] {
    const e = this.pendingEvents;
    this.pendingEvents = [];
    return e;
  }
}
